// Shared Module Action Rule evaluator — used by both ListView.jsx (List, one
// evaluation per selected row) and DetailHeaderCard.jsx (Detail, one
// evaluation for the single record on screen) so there is exactly one
// implementation of the 7 operators configured in
// pages/Modules/Admin/ModuleActionRulesSection.jsx, instead of two files that
// can silently drift apart (which is how Detail's `scope` operator went dead
// — it compared against a hardcoded literal instead of the real view scope).
//
// `scope` conditions compare against whatever "view scope" string the caller
// passes in (e.g. the active List tab key like "myJobs"/"allJobs"). Callers
// with no real scope concept (e.g. a Detail page) should pass `undefined` —
// a rule can then only match `scope` conditions if the admin never configured
// one for that context, same as any other field that isn't present.

// Resolves a rule's field path against the record, tolerating BOTH record
// shapes the two callers produce: List rows are the raw nested DB document
// (rule path "a.b" → record.a.b), while the Detail header flattens form-group
// fields under their configured field keys — which may be the dotted path
// itself ("a.b" as a literal key) or just the leaf ("b", e.g. the submission
// form's internalSubmissionStatusText vs the rule's nested
// internalSubmissionStatus.internalSubmissionStatusText). Trying literal →
// nested → leaf keeps one admin rule working against every caller with no
// per-module code.
function getNestedValue(record, fieldKey) {
  if (!fieldKey || typeof fieldKey !== 'string' || !record) return undefined;
  if (fieldKey in record) return record[fieldKey];
  const nested = fieldKey.split('.').reduce((value, key) => value?.[key], record);
  if (nested !== undefined) return nested;
  const leaf = fieldKey.split('.').pop();
  return leaf !== fieldKey ? record[leaf] : undefined;
}

function normalizeText(value) {
  return String(value ?? '').trim().toLowerCase();
}

function isEmptyRuleValue(value) {
  return value === undefined || value === null || value === ''
    || (Array.isArray(value) && value.length === 0);
}

export function conditionMatches(condition, record, scope) {
  const operator = condition?.operator;
  const actual = operator === 'scope' ? scope : getNestedValue(record, condition?.field);
  const expected = condition?.value;
  const normalizedActual = normalizeText(actual);
  const normalizedExpected = normalizeText(expected);

  if (operator === 'eq' || operator === 'scope') return normalizedActual === normalizedExpected;
  if (operator === 'ne') return normalizedActual !== normalizedExpected;
  if (operator === 'isEmpty') return isEmptyRuleValue(actual);
  if (operator === 'notEmpty') return !isEmptyRuleValue(actual);
  if (operator === 'fieldEq') {
    return normalizedActual === normalizeText(getNestedValue(record, condition?.compareField));
  }
  if (operator === 'contains') {
    if (Array.isArray(actual)) return actual.some((item) => normalizeText(item).includes(normalizedExpected));
    return normalizedActual.includes(normalizedExpected);
  }
  return false;
}

export function isRuleActionBlocked(actionKey, record, rules, scope) {
  const list = Array.isArray(rules) ? rules : [];
  if (!list.length) return false;
  return list.some((rule) => (
    rule?.isActive !== false
    && (rule?.blockedActions ?? []).map(String).includes(String(actionKey))
    && (rule?.conditions ?? []).every((condition) => conditionMatches(condition, record, scope))
  ));
}

// isActionBlocked — List view's original signature: blocked if ANY of the
// given records would have this action blocked (used for the bulk multi-select
// toolbar, where one action covers every selected row at once).
export function isActionBlocked(actionKey, records, rules, scope) {
  const list = Array.isArray(records) ? records : [];
  if (!list.length) return false;
  return list.some((record) => isRuleActionBlocked(actionKey, record, rules, scope));
}
